import React, {useEffect, useRef, useState} from "react";
import {currencyNoSymbolWithSpaceFormatter, extractDateBySecond} from "../../../utils";
import {ClipboardCopy} from 'lucide-react';
import {CloudDownloadOutlined} from "@ant-design/icons";
import {Button, message, QRCode} from "antd";
import html2canvas from "html2canvas";
import {APIv1} from "../../../api";
import {images} from "../../../constants";
import {
    addReceiptCanvasPadding,
    applyReceiptLogoCaptureStyles,
    DEFAULT_RECEIPT_LINE_LENGTH,
    getReceiptLogoCaptureStyles,
    getReceiptLineLength,
    RECEIPT_BASE_FONT_SIZE_REM,
    RECEIPT_BASE_SMALL_FONT_SIZE_PX,
    RECEIPT_BASE_TOTAL_FONT_SIZE_PX,
    RECEIPT_CAPTURE_SCALE,
    ReceiptDivider,
    ReceiptLine,
    ReceiptLineLengthContext,
    RECEIPT_TYPE_TEXT,
    waitForReceiptImages,
} from "./helper.receiptCheck";
import "./ReceiptCheck.scss";

const RECEIPT_TEXT = {
    download: "Yuklab olish",
    copy: "Copy",
    row1: "NKM S/R",
    row2: "Kassir",
    row3: "POS",
    row4: "STIR",
    row5: "CHEGIRMA",
    row6: "GTIN kod",
    row7: "MXIK KOD",
    row8: "Sh.j. QQS",
    row9: "Sh.j. QQS",
    row10: "Jami",
    row11: "QAYTIM",
    row12: "Naqd pul",
    row13: "Karta",
    row14: "Chek",
    row15: "FB",
    row16: "FM",
    row17: "Yuklanmoqda...",
    row18: "Pack kod",
    row19: "Noma'lum kompaniya",
    row20: "Noma'lum bozor",
    row21: "MARK kod",
};

const ReceiptCheck = ({orderData, orderId, type}) => {
    const QQSPercentage = 12;
    const receivedCardRaw = orderData.received_card / 100;
    const receivedCashRaw = orderData.received_cash / 100;
    const receivedTotalRaw = receivedCardRaw + receivedCashRaw;
    const [totalVatAmount, setTotalVatAmount] = useState(0);

    const receivedCard = currencyNoSymbolWithSpaceFormatter(receivedCardRaw);
    const receivedCash = currencyNoSymbolWithSpaceFormatter(receivedCashRaw);
    const receivedTotal = currencyNoSymbolWithSpaceFormatter(receivedTotalRaw);

    const token = React.useMemo(() => {
        const stored = JSON.parse(localStorage.getItem("user"));
        return stored?.token || null;
    }, []);

    const [products, setProducts] = useState([]);
    const [companyLogo, setComapnyLogo] = useState(true);
    const [companyData, setCompanyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const receiptRef = useRef(null);
    const [lineCharCount, setLineCharCount] = useState(null);
    const receiptLineLength = getReceiptLineLength(lineCharCount);
    const hasReceiptLineLength = receiptLineLength !== null;
    const receiptFontScale = hasReceiptLineLength ? DEFAULT_RECEIPT_LINE_LENGTH / receiptLineLength : 1;
    const receiptTypeText = RECEIPT_TYPE_TEXT[type] ?? type;

    useEffect(() => {
        if (!token) return;

        const fetchData = async () => {
            setLoading(true);
            setLineCharCount(null);
            try {
                const [ordersRes, companyRes, logoRes] = await Promise.all([
                    APIv1.get(`/order/related/products/${orderId}/`, {
                        headers: {Authorization: `Token ${token}`},
                    }),
                    APIv1.get(`/company/device/order/check/detail/?s=${orderData.cash_desc_serial}`, {
                        headers: {Authorization: `Token ${token}`},
                    }),
                    APIv1.get(`/company/profile/images/contents/${orderData.inn}`, {
                        headers: {Authorization: `Token ${token}`},
                    }),
                ]);

                setComapnyLogo(logoRes.data.data.logo || false);

                setProducts(
                    ordersRes.data.map((item) => ({
                        id: item.id,
                        name: item.name,
                        quantity: item.amount / 1000,
                        price: item.product_price / 100,
                        total: currencyNoSymbolWithSpaceFormatter(item.price / 100),
                        barcode: item.barcode || "-",
                        ikpu: item.class_code || "-",
                        labels: item.labels || [],
                        excise: item.excise
                            ? {label: "Акциз", value: currencyNoSymbolWithSpaceFormatter(item.excise / 100)}
                            : null,
                        vat: item.vat
                            ? {
                                label: `${RECEIPT_TEXT.row9}: ${item.vat_percent || QQSPercentage}%`,
                                value: currencyNoSymbolWithSpaceFormatter(item.vat / 100),
                            }
                            : null,
                        discountPercent: item.discount_percent,
                        packageCode: item.package_code,
                        unitName: item.unit_name,
                    }))
                );

                const calculatedVat = ordersRes.data.reduce((sum, item) => sum + (Number(item.vat) || 0), 0) / 100;
                setTotalVatAmount(calculatedVat);

                setCompanyData({
                    id: companyRes.data.id,
                    serial: companyRes.data.device_serial_number,
                    companyName: companyRes.data.company_name ?? RECEIPT_TEXT.row19,
                    marketName: companyRes.data?.market__name || orderData.market_name || RECEIPT_TEXT.row20,
                    address: companyRes.data.address,
                    lineCharCount: companyRes.data.line_char_count,
                    posNumber: companyRes.data.cash_desc_number,
                });
                setLineCharCount(companyRes.data.line_char_count);
            } catch (err) {
                console.error("Receipt fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [orderId, orderData?.cash_desc_serial, token]);

    const captureReceipt = async () => {
        if (!receiptRef.current) return;

        await waitForReceiptImages(receiptRef.current);
        const logoCaptureStyles = getReceiptLogoCaptureStyles(receiptRef.current);

        const canvas = await html2canvas(receiptRef.current, {
            scale: RECEIPT_CAPTURE_SCALE,
            useCORS: true,
            backgroundColor: "#ffffff",
            onclone: (clonedDocument) => {
                const clonedReceipt = clonedDocument.querySelector(".receipt");
                if (!clonedReceipt) return;

                applyReceiptLogoCaptureStyles(clonedReceipt, logoCaptureStyles);
            },
        });

        return addReceiptCanvasPadding(canvas);
    };

    const handleDownload = async () => {
        const canvas = await captureReceipt();
        if (!canvas) return;

        const link = document.createElement("a");
        link.download = `receipt-${orderId}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    // Copy receipt as PNG to clipboard
    const handleCopy = async () => {
        const canvas = await captureReceipt();
        if (!canvas) return;

        canvas.toBlob(async (blob) => {
            if (!blob) {
                message.error("Failed to copy receipt.");
                return;
            }
            try {
                await navigator.clipboard.write([new ClipboardItem({"image/png": blob})]);
                message.success("Receipt copied to clipboard!");
            } catch (err) {
                console.error("Copy failed:", err);
                message.error("Failed to copy receipt.");
            }
        });
    };

    if (loading || !hasReceiptLineLength) return <p>{RECEIPT_TEXT.row17}</p>;
    // logo should be above 2 buttons and center

    return (
        <div className="receipt__wrapper">
            <div className="receipt__actions">
                <div className="receipt__actions-inner">
                    <Button type="primary" onClick={handleDownload}>
                        {RECEIPT_TEXT.download} <CloudDownloadOutlined/>
                    </Button>
                    <Button onClick={handleCopy}>
                        {RECEIPT_TEXT.copy} <ClipboardCopy size={14}/>
                    </Button>
                </div>
            </div>
            <div className="receipt" ref={receiptRef}>
                {companyLogo ? (
                    <img
                        className="receipt__company__logo"
                        src={companyLogo}
                        alt="company logo"
                        crossOrigin="anonymous"
                    />
                ) : (
                    <img
                        className="receipt__company__logo"
                        src={images.newLogo}
                        alt="company logo"
                        crossOrigin="anonymous"
                    />
                )}
                <div
                    className="receipt__content"
                    style={{
                        "--receipt-line-length": receiptLineLength,
                        "--receipt-font-size": `${RECEIPT_BASE_FONT_SIZE_REM * receiptFontScale}rem`,
                        "--receipt-small-font-size": `${RECEIPT_BASE_SMALL_FONT_SIZE_PX * receiptFontScale}px`,
                        "--receipt-total-font-size": `${RECEIPT_BASE_TOTAL_FONT_SIZE_PX * receiptFontScale}px`,
                    }}
                >
                    <ReceiptLineLengthContext.Provider value={receiptLineLength}>
                        <div className="receipt__title">
                            <ReceiptLine text={companyData?.companyName} align="center"/>
                        </div>
                        <div className="receipt__subtitle">
                            <ReceiptLine text={companyData?.marketName} align="center"/>
                            <ReceiptLine text={companyData?.address} align="center"/>
                        </div>
                        {receiptTypeText ? (
                            <div className="receipt__type">{receiptTypeText}</div>
                        ) : null}

                        <ReceiptLine
                            left={`${RECEIPT_TEXT.row14}: ${orderData.number_result_url ?? ""}`}
                            right={extractDateBySecond(orderData.created_date, false) ?? "--/--/-- --:--"}
                        />
                        <ReceiptLine
                            left={`${RECEIPT_TEXT.row3}: ${companyData?.posNumber ?? "-"}`}
                            right={`${RECEIPT_TEXT.row2}: ${orderData.cashier ?? "-"}`}
                        />
                        <ReceiptLine
                            left={`${RECEIPT_TEXT.row4}: ${orderData.inn ?? "-"}`}
                            right={`${RECEIPT_TEXT.row1}: ${orderData.cash_desc_serial ?? "-"}`}
                        />

                        <ReceiptDivider/>

                        <div className="receipt__products">
                            {products.map((p) => (
                                <div key={p.id} className="receipt__product">
                                    <ReceiptLine
                                        className="receipt__product-name"
                                        text={p.name}
                                    />
                                    <div className="receipt__details">
                                        <ReceiptLine
                                            className="receipt__line--small"
                                            left={`${p.quantity} ${p.unitName ?? ""} X ${currencyNoSymbolWithSpaceFormatter(p.price)}`}
                                            right={p.total}
                                        />
                                        {/*{p.discountPercent && (*/}
                                        {/*    <ReceiptLine*/}
                                        {/*        className="red"*/}
                                        {/*        left={`${RECEIPT_TEXT.row5} (${Number(p.discountPercent)}% = ${receivedTotalRaw * (p.discountPercent / 100)}):`}*/}
                                        {/*        right={currencyNoSymbolWithSpaceFormatter(receivedTotalRaw - (receivedTotalRaw * p.discountPercent / 100))}*/}
                                        {/*    />*/}
                                        {/*)}*/}
                                        {p.vat && (
                                            <ReceiptLine
                                                // className="red"
                                                left={p.vat.label}
                                                right={p.vat.value}
                                            />
                                        )}
                                        <ReceiptLine
                                            left={`${RECEIPT_TEXT.row6}:`}
                                            right={p.barcode}
                                        />
                                        <ReceiptLine
                                            left={`${RECEIPT_TEXT.row7}:`}
                                            right={p.ikpu}
                                        />
                                        {p.excise && (
                                            <ReceiptLine
                                                left={`${p.excise.label}:`}
                                                right={p.excise.value}
                                            />
                                        )}
                                        <ReceiptLine
                                            left={`${RECEIPT_TEXT.row18}:`}
                                            right={p.packageCode ?? ""}
                                        />
                                        {p.labels.map((item, index) => (
                                            <ReceiptLine
                                                key={index}
                                                className="receipt__line--small"
                                                left={`${RECEIPT_TEXT.row21}:`}
                                                right={item}
                                            />
                                        ))}
                                    </div>
                                    <ReceiptDivider className="receipt__divider--no-margin"/>
                                </div>
                            ))}
                        </div>

                        <ReceiptLine
                            className="receipt__line--total"
                            left={`${RECEIPT_TEXT.row10}:`}
                            right={receivedTotal}
                        />
                        {totalVatAmount > 0 ? (
                            <ReceiptLine
                                left={`${RECEIPT_TEXT.row8}:`}
                                right={currencyNoSymbolWithSpaceFormatter(totalVatAmount)}
                            />
                        ) : null}

                        <ReceiptDivider symbol="=" className="receipt__divider--small-margin"/>

                        {/*{orderData.received_cash > 0 ? (*/}
                        {/*    <ReceiptLine*/}
                        {/*        left={`${RECEIPT_TEXT.row12}:`}*/}
                        {/*        right={receivedCash}*/}
                        {/*    />*/}
                        {/*) : orderData.received_card > 0 ? (*/}
                        {/*    <ReceiptLine*/}
                        {/*        left={`${RECEIPT_TEXT.row13}:`}*/}
                        {/*        right={receivedCard}*/}
                        {/*    />*/}
                        {/*) : null}*/}

                        {orderData.received_cash > 0 && (
                            <ReceiptLine
                                left={`${RECEIPT_TEXT.row12}:`}
                                right={receivedCash}
                            />
                        )}
                        {orderData.received_card > 0 && (
                            <ReceiptLine
                                left={`${RECEIPT_TEXT.row13}:`}
                                right={receivedCard}
                            />
                        )}

                        {orderData.change > 0 && (
                            <ReceiptLine
                                left={`${RECEIPT_TEXT.row11}:`}
                                right={currencyNoSymbolWithSpaceFormatter(orderData.change)}
                            />
                        )}

                        {[orderData.received_cash, orderData.received_card, orderData.change].some(v => v > 0) && (
                            <ReceiptDivider/>
                        )}

                        <ReceiptLine
                            left={`${RECEIPT_TEXT.row16}: ${orderData.fiscal_module ?? ""}`}
                            right={`${RECEIPT_TEXT.row15}: ${orderData.fiscal_sign ?? ""}`}
                        />
                    </ReceiptLineLengthContext.Provider>
                </div>

                <div className="receipt__qrcode">
                    {/* HIDE QR-CODE if type = prepaid AND version = v1 */}
                    {/* SHOW QR-CODE if type != prepaid OR version != v1 */}
                    {(orderData.type !== 'prepaid' || orderData.fbox_version !== 'v1') ? (
                        <QRCode
                            errorLevel="H"
                            size={200}
                            value={orderData.result_url || "-"}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default ReceiptCheck;
